import { CoreApi, ProductInfoComponent, ProductType } from '@inter-ikea-storageone/range';
import { ComponentIds, Entity, HierarchyUtils } from '@inter-ikea-core/core';
import { getEntitiesInSceneWithProductTypes } from '../filters/mainMenuProductFilters';
import { hideNoFramesInSceneErrorMessage } from 'rangeConfig/shared/rules/hideMenuRules';

export const hideTopPanelsMenu = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  const parentComponent = selectedEntity?.hasComponent(ComponentIds.PARENT_COMPONENT_ID);

  if (!selectedEntity || !parentComponent) {
    return true;
  }

  if (!coreApi.entities.isProductType(selectedEntity, ProductType.SMASTAD_FRAME)) {
    return true;
  }

  const frameList = HierarchyUtils.hierarchy(selectedEntity)
    .filter((c: Entity) => c.hasComponent(ComponentIds.FRAME_COMPONENT_ID))
    .filter(Boolean);

  if (frameList.length > 1) {
    return true;
  }

  return false;
};

export const hideChangingTableFunctionWidget = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();

  return selectedEntity
    ? !coreApi.entities.isOneOfProductTypes(selectedEntity, [
        ProductType.SMASTAD_CHANGING_TABLE,
        ProductType.SMASTAD_DESK
      ])
    : false;
};

export const hideClothRailOrganisers = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (!selectedEntity) {
    return true;
  }
  return (
    !coreApi.entities.isOneOfProductTypes(selectedEntity, [
      ProductType.SMASTAD_CLOTH_RAIL_ASM,
      ProductType.SMASTAD_CLOTH_RAIL_55_W_HOOKS_ASM
    ]) || coreApi.entities.getEntityComSize(selectedEntity)?.depth !== 550
  );
};

export const hideFrameDrawerWidget = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();

  return selectedEntity
    ? !coreApi.entities.isOneOfProductTypes(selectedEntity, [ProductType.SMASTAD_FRAME_NA])
    : false;
};

export const hideAgeGroupToggle = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  return selectedEntity && selectedEntity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
    ? false
    : true;
};

// Error to be displayed when user tries to add legs but some frames are pull out frames
export const hideSmastadAddingLegsToMixedPulloutFrameErrorMessage = (coreApi: CoreApi) => {
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

  const pullOutFramesInScene = getEntitiesInSceneWithProductTypes(framesInScene, [
    ProductType.SMASTAD_PULL_OUT_FRAME_108_ASM,
    ProductType.SMASTAD_PULL_OUT_FRAME_196_ASM,
    ProductType.SMASTAD_PULL_OUT_FRAME_DRAWER
  ]);

  const mixed =
    pullOutFramesInScene.length > 0 && framesInScene.length - pullOutFramesInScene.length > 0;

  return !mixed;
};

// Show this error message user tries to add legs but nothing happens since all frames are pull out frames
export const hideSmastadAddingLegsToPulloutFrameErrorMessage = (coreApi: CoreApi) => {
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

  const pullOutFramesInScene = getEntitiesInSceneWithProductTypes(framesInScene, [
    ProductType.SMASTAD_PULL_OUT_FRAME_108_ASM,
    ProductType.SMASTAD_PULL_OUT_FRAME_196_ASM,
    ProductType.SMASTAD_PULL_OUT_FRAME_DRAWER
  ]);

  return (
    pullOutFramesInScene?.length === 0 ||
    !hideNoFramesInSceneErrorMessage(coreApi) ||
    !hideSmastadAddingLegsToMixedPulloutFrameErrorMessage(coreApi)
  );
};

// Error to be displayed when user tries to change doors but all frames have included doors
export const hideSmastadAllFramesIncludedDoorsErrorMessage = (coreApi: CoreApi) => {
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

  const smastadFramesWithIncludedDoors = framesInScene?.filter(
    f =>
      f.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.globalArticleNbr ===
      'ART-80436965'
  )?.length;

  const allFramesIncludedDoors =
    smastadFramesWithIncludedDoors > 0 && smastadFramesWithIncludedDoors === framesInScene?.length;

  return !allFramesIncludedDoors;
};

// Error to be displayed when user tries to change doors but some frames have included doors
export const hideSmastadMixedFramesIncludedDoorsErrorMessage = (coreApi: CoreApi) => {
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

  const smastadFramesWithIncludedDoors = framesInScene?.filter(
    f =>
      f.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.globalArticleNbr ===
      'ART-80436965'
  )?.length;

  const mixedFramesIncludedDoors =
    smastadFramesWithIncludedDoors > 0 && framesInScene.length - smastadFramesWithIncludedDoors > 0;

  return !mixedFramesIncludedDoors;
};

export const hideLoftBedOrientationWidget = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  return selectedEntity
    ? !coreApi.entities.isProductType(selectedEntity, ProductType.SMASTAD_LOFT_BED)
    : true;
};
